//@flow
import {
    pjpAnalyticsDashboards,
    pjpMetricsMetadata,
    pjpMetrics,
    createPjpMetricDashboard
} from 'common/middleware/api';

import type { Dispatch } from 'redux';

import { injectError } from 'common/redux/actions/error';
import { ApiError } from 'common/modules/api';

import type { PjpMetricResponseModelType } from 'common/models/flow/PjpMetricResponse';
import type { PjpInsightsMetadata } from 'common/models/internal/PjpInsightsMetadata';
import type { PjpMetricQueryModelType } from 'common/models/flow/PjpMetricQuery';
import { PjpInsightsDashboard } from 'common/models/internal/PjpInsightsDashboard';

type Params = {
    clientId: string,
    graphId?: string,
    includeExistingPlan?: boolean,
    pjpId?: string
};

export default class PjpInsightsActions {
    static FETCH_ALL_PJP_DASHBOARDS_START = 'FETCH_ALL_PJP_DASHBOARDS_START';
    static FETCH_ALL_PJP_DASHBOARDS_SUCCESS =
        'FETCH_ALL_PJP_DASHBOARDS_SUCCESS';
    static FETCH_ALL_PJP_DASHBOARDS_FAILURE =
        'FETCH_ALL_PJP_DASHBOARDS_FAILURE';
    static FETCH_PJP_INSIGHT_DASHBOARD_START =
        'FETCH_PJP_INSIGHT_DASHBOARD_START';
    static FETCH_PJP_INSIGHT_DASHBOARD_SUCCESS =
        'FETCH_PJP_INSIGHT_DASHBOARD_SUCCESS';
    static FETCH_PJP_INSIGHT_DASHBOARD_FAILURE =
        'FETCH_PJP_INSIGHT_DASHBOARD_FAILURE';
    static FETCH_PJP_METRICS_METADATA_START =
        'FETCH_PJP_METRICS_METADATA_START';
    static FETCH_PJP_METRICS_METADATA_SUCCESS =
        'FETCH_PJP_METRICS_METADATA_SUCCESS';
    static FETCH_PJP_METRICS_METADATA_FAILURE =
        'FETCH_PJP_METRICS_METADATA_FAILURE';
    static FETCH_PJP_METRIC_START = 'FETCH_PJP_METRIC_START';
    static FETCH_PJP_METRIC_SUCCESS = 'FETCH_PJP_METRIC_SUCCESS';
    static FETCH_PJP_METRIC_FAILURE = 'FETCH_PJP_METRIC_FAILURE';
    static ADD_PJP_INSIGHTS_DASHBOARD_START =
        'ADD_PJP_INSIGHTS_DASHBOARD_START';
    static ADD_PJP_INSIGHTS_DASHBOARD_SUCCESS =
        'ADD_PJP_INSIGHTS_DASHBOARD_SUCCESS';
    static ADD_PJP_INSIGHTS_DASHBOARD_FAILURE =
        'ADD_PJP_INSIGHTS_DASHBOARD_FAILURE';
    static FETCH_CONFIGURED_PJP_METRIC_SUCCESS =
        'FETCH_CONFIGURED_PJP_METRIC_SUCCESS';
    static FETCH_CONFIGURED_PJP_METRIC_START =
        'FETCH_CONFIGURED_PJP_METRIC_START';
    static FETCH_CONFIGURED_PJP_METRIC_FAILURE =
        'FETCH_CONFIGURED_PJP_METRIC_FAILURE';
    static CLEAR_CONFIGURED_METRIC = 'CLEAR_CONFIGURED_METRIC';
    static DELETE_PJP_INSIGHTS_DASHBOARD_START =
        'DELETE_PJP_INSIGHTS_DASHBOARD_START';
    static DELETE_PJP_INSIGHTS_DASHBOARD_SUCCESS =
        'DELETE_PJP_INSIGHTS_DASHBOARD_SUCCESS';
    static DELETE_PJP_INSIGHTS_DASHBOARD_FAILURE =
        'DELETE_PJP_INSIGHTS_DASHBOARD_FAILURE';

    static clearConfiguredMetric = () => {
        return {
            type: PjpInsightsActions.CLEAR_CONFIGURED_METRIC
        };
    };

    static fetchAllDashboardsStart = (params: Params) => {
        return {
            type: PjpInsightsActions.FETCH_ALL_PJP_DASHBOARDS_START,
            params
        };
    };

    static fetchAllDashboardsSuccess = (
        params: Params,
        dashboards: Array<PjpInsightsDashboard>
    ) => {
        return {
            type: PjpInsightsActions.FETCH_ALL_PJP_DASHBOARDS_SUCCESS,
            params,
            entity: dashboards
        };
    };

    static fetchAllDashboardsFailure = (error: Error) => {
        return {
            type: PjpInsightsActions.FETCH_ALL_PJP_DASHBOARDS_FAILURE,
            error
        };
    };

    static fetchPjpMetricsMetadataStart = (params: Params) => {
        return {
            type: PjpInsightsActions.FETCH_PJP_METRICS_METADATA_START,
            params
        };
    };

    static fetchPjpMetricsMetadataSuccess = (
        params: Params,
        metricsMetadata: Array<PjpInsightsMetadata>
    ) => {
        return {
            type: PjpInsightsActions.FETCH_PJP_METRICS_METADATA_SUCCESS,
            params,
            metricsMetadata
        };
    };

    static fetchPjpMetricsMetadataFailure = (params: Params, error: Error) => {
        return {
            type: PjpInsightsActions.FETCH_PJP_METRICS_METADATA_FAILURE,
            params,
            error
        };
    };

    static fetchPjpMetricStart = (
        params: Params,
        queries: Array<PjpMetricQueryModelType>
    ) => {
        return {
            type: PjpInsightsActions.FETCH_PJP_METRIC_START,
            params,
            queries
        };
    };

    static fetchPjpMetricSuccess = (
        params: Params,
        queries: Array<PjpMetricQueryModelType>,
        metricResponse: PjpMetricResponseModelType
    ) => {
        return {
            type: PjpInsightsActions.FETCH_PJP_METRIC_SUCCESS,
            params,
            queries,
            metricResponse
        };
    };

    static fetchPjpMetricFailure = (
        params: Params,
        queries: Array<PjpMetricQueryModelType>,
        error: Error
    ) => {
        return {
            type: PjpInsightsActions.FETCH_PJP_METRIC_FAILURE,
            queries,
            error,
            params
        };
    };

    static addPjpInsightsDashboardStart = (params: Params) => {
        return {
            type: PjpInsightsActions.ADD_PJP_INSIGHTS_DASHBOARD_START,
            params
        };
    };

    static addPjpInsightsDashboardSuccess = (
        params: Params,
        pjpInsightsDashboard: PjpInsightsDashboard
    ) => {
        return {
            type: PjpInsightsActions.ADD_PJP_INSIGHTS_DASHBOARD_SUCCESS,
            params,
            pjpInsightsDashboard
        };
    };

    static addPjpInsightsDashboardFailure = (params: Params, error: Error) => {
        return {
            type: PjpInsightsActions.ADD_PJP_INSIGHTS_DASHBOARD_FAILURE,
            error
        };
    };

    static fetchConfiguredMetricStart = (
        params: Params,
        queries: Array<PjpMetricQueryModelType>
    ) => {
        return {
            type: PjpInsightsActions.FETCH_CONFIGURED_PJP_METRIC_START,
            params,
            queries
        };
    };

    static fetchConfigureMetricSuccess = (
        params: Params,
        queries: Array<PjpMetricQueryModelType>,
        metricResponse: PjpMetricResponseModelType
    ) => {
        return {
            type: PjpInsightsActions.FETCH_CONFIGURED_PJP_METRIC_SUCCESS,
            params,
            queries,
            metricResponse
        };
    };

    static fetchConfiguredMetricFailure = (params: Params, error: Error) => {
        return {
            type: PjpInsightsActions.FETCH_CONFIGURED_PJP_METRIC_FAILURE,
            params,
            error
        };
    };

    static deletePjpInsightsDashboardStart = (params: Params) => {
        return {
            type: PjpInsightsActions.DELETE_PJP_INSIGHTS_DASHBOARD_START,
            params
        };
    };

    static deletePjpInsightsDashboardSuccess = (params: Params) => {
        return {
            type: PjpInsightsActions.DELETE_PJP_INSIGHTS_DASHBOARD_SUCCESS,
            params
        };
    };

    static deletePjpInsightsDashboardFailure = (params: Params) => {
        return {
            type: PjpInsightsActions.DELETE_PJP_INSIGHTS_DASHBOARD_FAILURE,
            params
        };
    };

    static fetchPjpMetric = (
        clientId: string,
        pjpId: string,
        metricId: string,
        groupBy: Array<string>,
        aggregationType: string,
        graphId: string,
        includeExistingPlan: boolean,
        isEdit: ?boolean,
        flushExisting: boolean
    ) => {
        return (dispatch: Dispatch<*>) => {
            const params = {
                clientId,
                pjpId,
                graphId,
                includeExistingPlan,
                flushExisting
            };

            const queries = [
                {
                    metric: metricId,
                    groupBy,
                    aggregationType
                }
            ];
            const body = {
                queries
            };
            let apiParams;
            if (includeExistingPlan) {
                apiParams = {
                    clientId,
                    pjpId,
                    includeExistingPlan
                };
            } else {
                apiParams = {
                    clientId,
                    pjpId
                };
            }

            if (isEdit) {
                dispatch(
                    PjpInsightsActions.fetchConfiguredMetricStart(
                        params,
                        queries
                    )
                );
            } else {
                dispatch(
                    PjpInsightsActions.fetchPjpMetricStart(params, queries)
                );
            }
            return pjpMetrics
                .post(apiParams, body)
                .then(pjpMetricsResponse => {
                    if (isEdit) {
                        dispatch(
                            PjpInsightsActions.fetchConfigureMetricSuccess(
                                params,
                                queries,
                                pjpMetricsResponse
                            )
                        );
                    } else {
                        dispatch(
                            PjpInsightsActions.fetchPjpMetricSuccess(
                                params,
                                queries,
                                pjpMetricsResponse
                            )
                        );
                    }
                    return;
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                    } else {
                        throw error;
                    }
                    if (isEdit) {
                        dispatch(
                            PjpInsightsActions.fetchConfiguredMetricFailure(
                                params,
                                error
                            )
                        );
                    } else {
                        dispatch(
                            PjpInsightsActions.fetchPjpMetricFailure(
                                params,
                                queries,
                                error
                            )
                        );
                    }
                });
        };
    };

    static fetchPjpMetricsMetadata = (clientId: string): Function => {
        return dispatch => {
            const params = { clientId };
            dispatch(PjpInsightsActions.fetchPjpMetricsMetadataStart(params));
            pjpMetricsMetadata
                .fetch(params)
                .then(metricsMetadata => {
                    dispatch(
                        PjpInsightsActions.fetchPjpMetricsMetadataSuccess(
                            params,
                            metricsMetadata.metadata
                        )
                    );
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                    } else {
                        throw error;
                    }
                    dispatch(
                        PjpInsightsActions.fetchPjpMetricsMetadataFailure(
                            params,
                            error
                        )
                    );
                });
        };
    };

    static fetchAllPjpInsightsDashboards = (clientId: string): Function => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const params = { clientId };
                dispatch(PjpInsightsActions.fetchAllDashboardsStart(params));
                pjpAnalyticsDashboards
                    .fetch(params)
                    .then(dashboards => {
                        const parsedDashboards = dashboards.dashboards.map(
                            dashboard => {
                                return {
                                    ...dashboard,
                                    dashboardData: dashboard.dashboardData
                                        ? JSON.parse(dashboard.dashboardData)
                                        : null,
                                    queryValidations: dashboard.queryValidations
                                        ? JSON.parse(dashboard.queryValidations)
                                        : null
                                };
                            }
                        );
                        dispatch(
                            PjpInsightsActions.fetchAllDashboardsSuccess(
                                params,
                                parsedDashboards
                            )
                        );
                        resolve(parsedDashboards);
                    })
                    .catch(error => {
                        if (error instanceof ApiError) {
                            dispatch(injectError(error));
                        } else {
                            throw error;
                        }
                        dispatch(
                            PjpInsightsActions.fetchAllDashboardsFailure(error)
                        );
                        reject(error);
                    });
            });
        };
    };

    static deletePjpInsightsDashboard = (
        clientId: string,
        dashboardId: string
    ): Function => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const params = { clientId, dashboardId };
                const parsedDashboard = {
                    dashboardId,
                    dashboardData: null,
                    active: false,
                    queryValidations: null
                };
                dispatch(
                    PjpInsightsActions.deletePjpInsightsDashboardStart(params)
                );
                createPjpMetricDashboard
                    .put(params, parsedDashboard)
                    .then(updatedPjpDashboardResponse => {
                        dispatch(
                            PjpInsightsActions.deletePjpInsightsDashboardSuccess(
                                params
                            )
                        );
                        resolve(updatedPjpDashboardResponse);
                    })
                    .catch((error: Error) => {
                        reject(error);
                        if (error instanceof ApiError) {
                            dispatch(injectError(error));
                        } else {
                            throw error;
                        }
                        dispatch(
                            PjpInsightsActions.deletePjpInsightsDashboardFailure(
                                params
                            )
                        );
                    });
            });
        };
    };

    static addPjpInsightsDashboard = (
        clientId: string,
        dashboard: PjpInsightsDashboard
    ): Function => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const params = { clientId, dashboardId: dashboard.dashboardId };
                dispatch(
                    PjpInsightsActions.addPjpInsightsDashboardStart(params)
                );
                let parsedDashboard = dashboard.toJS();
                parsedDashboard = {
                    ...parsedDashboard,
                    dashboardData: JSON.stringify(parsedDashboard.dashboardData)
                };
                createPjpMetricDashboard
                    .put(params, parsedDashboard)
                    .then(addPjpDashboardResponse => {
                        PjpInsightsActions.fetchAllPjpInsightsDashboards(
                            clientId
                        );
                        dispatch(
                            PjpInsightsActions.addPjpInsightsDashboardSuccess(
                                params,
                                addPjpDashboardResponse
                            )
                        );
                        resolve(addPjpDashboardResponse);
                    })
                    .catch((error: Error) => {
                        reject(error);
                        if (error instanceof ApiError) {
                            dispatch(injectError(error));
                        } else {
                            throw error;
                        }
                        dispatch(
                            PjpInsightsActions.addPjpInsightsDashboardFailure(
                                params,
                                error
                            )
                        );
                    });
            });
        };
    };
}
